import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Header from '@sitebase/pre-integrated-header';
import { MediaProvider } from '@sitebase/media-context';
import { publish } from '@wh/message-bus';
import { throttle } from 'throttle-debounce';
import { AssetsProvider } from '@sitebase/assets-context';
import { TranslatorProvider } from '@wh/ui-translations';
import svgIconsResolver from '../../IconResolver';
import renderers from '../../renderers';
import { initSessionFlow } from '../../flows/session';
import { initSubscription, setupDefaults, openSearch } from '../../flows/app';
import { SCROLL_DELTA, HEADER_HEIGHT } from '../../flows/app/consts';
import { LANGUAGE_SELECT } from '../../topics';
import { isQuickDepositDisabled } from '../../utils/isQuickDepositDisabled';

class App extends Component {
  static propTypes = {
    /* eslint-disable react/forbid-prop-types */
    bootstrapFeatures: PropTypes.array,
    bootstrapConfig: PropTypes.object,
    headerConfig: PropTypes.object,
    translations: PropTypes.object,
    openbetLoginUrl: PropTypes.string.isRequired,
    serviceUrl: PropTypes.string.isRequired,
    locale: PropTypes.string,
    /* eslint-enable react/forbid-prop-types */
    deviceType: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),
    clientSide: PropTypes.bool,
    domain: PropTypes.string,
  };

  static defaultProps = {
    deviceType: 'desktop',
    clientSide: false,
  };

  state = {
    deviceType: this.props.deviceType === 'tablet' ? 'desktop' : this.props.deviceType,
    shifted: false,
    hamburgerState: 'inactive',
    collapsed: false,
  };

  componentDidMount() {
    const { locale, clientSide, headerConfig, domain } = this.props;

    if (clientSide) {
      this.unsubscribeWorkflows = initSubscription({
        disableQuickDeposit: isQuickDepositDisabled(
          headerConfig.disableQuickDeposit,
          headerConfig.enableQuickDepositCookie
        ),
        isMyAccountEnabled: this.isMyAccountEnabled(),
        links: headerConfig.links,
        onCloseSideMenu: this.onCloseSideMenu,
        onLanguageSwitchRequested: this.onLanguageSwitchRequested,
        onOpenSideMenu: this.onOpenSideMenu,
      });
      this.unsubscribeSessionWorkflow = initSessionFlow(this.props.openbetLoginUrl, headerConfig.obSession, domain);
      this.handleResponsiveness();
      setupDefaults({ locale });
      window.addEventListener('scroll', this.onScrollHandler);
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeWorkflows) {
      this.unsubscribeWorkflows();
    }
    if (this.unsubscribeSessionWorkflow) {
      this.unsubscribeSessionWorkflow();
    }
    window.removeEventListener('resize', this.changeTabletLayout);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onLanguageSwitchRequested = ({ code }) => {
    const { locale } = this.props;
    if (locale !== code) {
      publish(LANGUAGE_SELECT, code);
    }
  };

  onOpenSideMenu = () => {
    this.setState({ shifted: true, hamburgerState: 'active' });
  };

  onCloseSideMenu = () => {
    this.setState({ shifted: false, hamburgerState: 'inactive' });
  };

  onScrollHandler = throttle(100, () => {
    const currentYScrollPosition = window.scrollY;
    if (this.state.deviceType === 'mobile') {
      this.checkCollapsed(currentYScrollPosition, this.lastYScrollPosition);
    } else if (this.state.collapsed) {
      this.setState({ collapsed: false });
    }
    this.lastYScrollPosition = currentYScrollPosition;
  });

  isMyAccountEnabled() {
    const {
      headerConfig: { myAccount },
    } = this.props;

    return myAccount.isEnabled;
  }

  checkCollapsed = (currentYScrollPosition, lastYScrollPosition) => {
    const deltaY = lastYScrollPosition - currentYScrollPosition;
    const direction = deltaY > 0 ? 'UP' : 'DOWN';
    const isOnTop = currentYScrollPosition <= HEADER_HEIGHT;
    const isFastMove = deltaY > SCROLL_DELTA;
    const isCollapsed = this.state.collapsed;

    if (direction === 'DOWN' && !isOnTop && !isCollapsed) {
      this.setState({ collapsed: true });
    } else if (direction === 'UP' && (isFastMove || isOnTop) && isCollapsed) {
      this.setState({ collapsed: false });
    }
  };

  handleResponsiveness = () => {
    if (this.props.deviceType === 'tablet') {
      this.changeTabletLayout();
      window.addEventListener('resize', this.changeTabletLayout);
    }
  };

  changeTabletLayout = throttle(100, () => {
    const deviceType = window.innerWidth < 768 ? 'mobile' : 'desktop';
    if (this.state.deviceType !== deviceType) {
      this.setState({ deviceType });
    }
  });

  lastYScrollPosition = 0;

  render() {
    const { headerConfig, translations } = this.props;
    const { deviceType: type, hamburgerState, shifted, collapsed } = this.state;

    const headerProps = {
      collapsed,
      hamburgerState,
      renderers,
      shifted,
      type,
      config: headerConfig,
      searchOpen: false,
      onSearchOpen: openSearch,
    };

    return (
      <TranslatorProvider translations={translations}>
        <AssetsProvider resolver={svgIconsResolver}>
          <MediaProvider device={type}>
            <Header {...headerProps} />
          </MediaProvider>
        </AssetsProvider>
      </TranslatorProvider>
    );
  }
}

export default App;
